// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { ObjectType, ResponderOutput } from 'thehive-sdk';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import { useListResponders } from '@Queries/responderQueries';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { columnsRenderer } from '@Hooks/useConfigList/helper/computeHeader';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import client from '@Client';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ICONS } from '@Constants/iconsApp';
import THTable from '@Molecules/THTable/THTable';
import { SortButton } from '@Atoms/Buttons';
import { SortDirections } from '@Hooks/useSort';

import './ResponderList.scss';

interface ResponderListProps {
    objectId: string;
    objectType: ObjectType;
}

const ResponderList = ({ objectId, objectType }: ResponderListProps): ReactElement => {
    const i = useLocalMessage();

    const { data: respondersList } = useListResponders(objectType, objectId);

    const [filter, setFilter] = useState('');
    const [order, setOrder] = useState<SortDirections>(SortDirections.ASC);

    const handleConfirm = useCallback(
        (responderId: string): void => {
            client.ResponderController.launch(objectType, objectId, responderId)
                .then(() => openMessage('success', i('notification.responder.launch')))
                .catch(handleNotificationError);
        },
        [i, objectId, objectType],
    );

    const columns = columnsRenderer<ResponderOutput>([
        {
            title: (
                <ColumnTitle>
                    <SortButton
                        label={i('common.name')}
                        value={order}
                        onSortChange={(newDirection) => setOrder(newDirection)}
                    />
                </ColumnTitle>
            ),
            render: ({ name, description }) => (
                <div className="responder-list__name">
                    {name}
                    <div className="responder-list__description">{description}</div>
                </div>
            ),
        },
        {
            render: ({ id }) => (
                <THPopConfirm
                    title={i('responder.drawer.confirm')}
                    placement="bottom"
                    okText={i('general.ok')}
                    cancelText={i('general.cancel')}
                    onConfirm={() => handleConfirm(id)}
                    icon={<ICONS.FileSearchOutlined />}
                >
                    <THButton type="primary">{i('responder.drawer.launch')}</THButton>
                </THPopConfirm>
            ),
        },
    ]);

    const dataSourceFiltered = useMemo(
        () =>
            respondersList
                ?.filter(
                    (responder) =>
                        responder.name.toLowerCase().includes(filter.toLowerCase()) ||
                        responder.description.toLowerCase().includes(filter.toLowerCase()),
                )
                .sort(({ name: nameA }, { name: nameB }) => {
                    const comp = nameA.localeCompare(nameB);
                    if (order === SortDirections.ASC) {
                        return comp;
                    }
                    if (order === SortDirections.DESC) {
                        return -comp;
                    }
                    return 0;
                }),
        [respondersList, filter, order],
    );

    return (
        <div className="responder-list">
            <THInput
                placeholder={i('responder.drawer.search-field')}
                value={filter}
                onChange={(event): void => setFilter(event.target.value.trimStart())}
            />

            <THTable columns={columns} dataSource={dataSourceFiltered} rowKey="id" />
        </div>
    );
};

export default ResponderList;
